import React, { useState, useEffect } from 'react';
import { documentsApi, productsApi, depotsApi, preparationsApi } from '../lib/api';
import api, { fetchBlob } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/errorUtils';
import { 
  FileText, Truck, Download, Plus, X, AlertTriangle,
  Printer, Eye, Calendar, Building2, Users, ClipboardList
} from 'lucide-react';

const STORAGE_CLASSES = ['1.1G', '1.2G', '1.3G', '1.4G', '1.4S'];

const DocumentsPage = () => {
  const [products, setProducts] = useState([]);
  const [depots, setDepots] = useState([]);
  const [clients, setClients] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTransportDialog, setShowTransportDialog] = useState(false);
  const [generating, setGenerating] = useState(false);

  // BL state
  const [showBLDialog, setShowBLDialog] = useState(false);
  const [preparations, setPreparations] = useState([]);
  const [blPrepId, setBlPrepId] = useState('');
  const [generatingBL, setGeneratingBL] = useState(false);

  const [transportForm, setTransportForm] = useState({
    reference: '',
    expeditor_name: '',
    expeditor_address: '',
    expeditor_phone: '',
    recipient_name: '',
    recipient_address: '',
    carrier_name: '',
    transport_date: new Date().toISOString().split('T')[0],
    items: [],
  });

  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, depotsRes, clientsRes, companyRes, prepsRes] = await Promise.all([
        productsApi.getAll({ limit: 1000 }),
        depotsApi.getAll(),
        api.get('/clients').catch(() => ({ data: [] })),
        api.get('/company-info').catch(() => ({ data: null })),
        preparationsApi.getAll().catch(() => ({ data: [] })),
      ]);

      setProducts(productsRes.data.items || []);
      setDepots(depotsRes.data);
      setClients(clientsRes.data || []);
      setPreparations(Array.isArray(prepsRes.data) ? prepsRes.data : []);
      const comp = companyRes.data;
      setCompanyInfo(comp);

      if (comp) {
        setTransportForm(prev => ({
          ...prev,
          expeditor_name: comp.company_name || '',
          expeditor_address: [comp.address, `${comp.postal_code || ''} ${comp.city || ''}`].filter(Boolean).join('\n'),
          expeditor_phone: comp.mobile1 || comp.phone1 || '',
          carrier_name: comp.company_name || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBL = async () => {
    if (!blPrepId) { toast.error('Selectionnez une preparation'); return; }
    setGeneratingBL(true);
    try {
      const res = await fetchBlob('POST', '/documents/delivery-note', { preparation_id: blPrepId });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      toast.success('Bon de livraison genere');
      setShowBLDialog(false);
    } catch (err) {
      toast.error('Erreur lors de la generation du BL');
    }
    setGeneratingBL(false);
  };


  const handleSelectClient = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setTransportForm(prev => ({
        ...prev,
        recipient_name: client.company_name || '',
        recipient_address: [client.delivery_address, `${client.delivery_postal_code || ''} ${client.delivery_city || ''}`].filter(Boolean).join('\n'),
      }));
    }
  };

  const handleAddItem = () => {
    if (!selectedProduct || !selectedQuantity) {
      toast.error('Sélectionnez un produit et une quantité');
      return;
    }

    const product = products.find(p => p.id === selectedProduct);
    if (!product) return;

    setTransportForm({
      ...transportForm,
      items: [...transportForm.items, {
        product_id: selectedProduct,
        product: product,
        quantity: parseInt(selectedQuantity),
      }]
    });

    setSelectedProduct('');
    setSelectedQuantity('');
  };

  const handleRemoveItem = (index) => {
    setTransportForm({
      ...transportForm,
      items: transportForm.items.filter((_, i) => i !== index)
    });
  };

  const calculateTotals = () => {
    let totalNEC = 0;
    let totalWeight = 0;
    const necByClass = {};

    transportForm.items.forEach(item => {
      const nec = (item.product?.nec || 0) * item.quantity;
      const weight = (item.product?.gross_weight || 0) * item.quantity;
      totalNEC += nec;
      totalWeight += weight;

      const riskClass = item.product?.risk_class || 'Non classé';
      necByClass[riskClass] = (necByClass[riskClass] || 0) + nec;
    });

    return { totalNEC, totalWeight, necByClass };
  };

  const handleGenerateDocument = async (e) => {
    e.preventDefault();

    if (transportForm.items.length === 0) {
      toast.error('Ajoutez au moins un produit');
      return;
    }

    setGenerating(true);
    try {
      const response = await documentsApi.generateTransport({
        ...transportForm,
        items: transportForm.items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity
        }))
      });

      // Download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bon_transport_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Document généré avec succès');
      setShowTransportDialog(false);
    } catch (error) {
      toast.error('Erreur lors de la génération du document');
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Documents</h1>
          <p className="text-zinc-400 text-sm mt-1">Génération de documents réglementaires ADR</p>
        </div>
      </div>

      {/* Document Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Transport ADR */}
        <Card className="bg-zinc-900/50 border-zinc-800 card-hover">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Truck className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <CardTitle className="text-white">Bon de Transport ADR</CardTitle>
                <p className="text-sm text-zinc-400">Document réglementaire</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-zinc-400">
              Génère un bon de transport conforme à la réglementation ADR pour le transport de matières dangereuses de classe 1.
            </p>
            <ul className="text-xs text-zinc-500 space-y-1">
              <li>• Identification expéditeur/destinataire</li>
              <li>• Liste des matières avec codes ONU</li>
              <li>• Calcul automatique NEC totale</li>
              <li>• Déclaration ADR obligatoire</li>
            </ul>
            <Dialog open={showTransportDialog} onOpenChange={setShowTransportDialog}>
              <DialogTrigger asChild>
                <Button className="w-full bg-amber-500 hover:bg-amber-400 text-black">
                  <FileText className="w-4 h-4 mr-2" /> Générer
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white font-heading flex items-center gap-2">
                    <Truck className="w-5 h-5 text-amber-400" />
                    Bon de Transport ADR
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleGenerateDocument} className="space-y-6">
                  {/* Reference */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Référence commande</Label>
                    <Input
                      value={transportForm.reference}
                      onChange={(e) => setTransportForm({...transportForm, reference: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-white"
                      placeholder="Ex: Tonnay Charente V"
                    />
                  </div>

                  {/* Expeditor */}
                  <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                    <h3 className="text-sm font-semibold text-amber-400 mb-3">EXPÉDITEUR</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Raison sociale *</Label>
                        <Input
                          value={transportForm.expeditor_name}
                          onChange={(e) => setTransportForm({...transportForm, expeditor_name: e.target.value})}
                          className="bg-zinc-900 border-zinc-700 text-white"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Adresse *</Label>
                        <Input
                          value={transportForm.expeditor_address}
                          onChange={(e) => setTransportForm({...transportForm, expeditor_address: e.target.value})}
                          className="bg-zinc-900 border-zinc-700 text-white"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Recipient */}
                  <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                    <h3 className="text-sm font-semibold text-cyan-400 mb-3 flex items-center gap-2">DESTINATAIRE
                      {clients.length > 0 && (
                        <Select onValueChange={handleSelectClient}>
                          <SelectTrigger className="w-[250px] bg-zinc-900 border-zinc-700 text-white h-7 text-xs">
                            <SelectValue placeholder="Charger un client..." />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[200px]">
                            {clients.map(cl => (
                              <SelectItem key={cl.id} value={cl.id} className="text-white text-xs">
                                {cl.company_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Raison sociale *</Label>
                        <Input
                          value={transportForm.recipient_name}
                          onChange={(e) => setTransportForm({...transportForm, recipient_name: e.target.value})}
                          className="bg-zinc-900 border-zinc-700 text-white"
                          placeholder="Client / Lieu de tir"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Adresse *</Label>
                        <Input
                          value={transportForm.recipient_address}
                          onChange={(e) => setTransportForm({...transportForm, recipient_address: e.target.value})}
                          className="bg-zinc-900 border-zinc-700 text-white"
                          placeholder="Adresse de livraison"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Transport Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Date de transport</Label>
                      <Input
                        type="date"
                        value={transportForm.transport_date}
                        onChange={(e) => setTransportForm({...transportForm, transport_date: e.target.value})}
                        className="bg-zinc-950 border-zinc-800 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Transporteur</Label>
                      <Input
                        value={transportForm.carrier_name}
                        onChange={(e) => setTransportForm({...transportForm, carrier_name: e.target.value})}
                        className="bg-zinc-950 border-zinc-800 text-white"
                        placeholder="Nom du transporteur"
                      />
                    </div>
                  </div>

                  {/* Products */}
                  <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                    <h3 className="text-sm font-semibold text-fuchsia-400 mb-3">MARCHANDISES DANGEREUSES</h3>
                    <div className="flex gap-2 mb-4">
                      <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                        <SelectTrigger className="flex-1 bg-zinc-900 border-zinc-700 text-white">
                          <SelectValue placeholder="Sélectionner un produit" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[200px]">
                          {products.filter(p => p.un_code).map(product => (
                            <SelectItem key={product.id} value={product.id} className="text-white">
                              <span className="font-mono text-red-400">{product.un_code}</span>
                              <span className="mx-2">-</span>
                              <span className="text-cyan-400">{product.reference}</span>
                              <span className="text-zinc-400 ml-2">{product.designation?.substring(0, 20)}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        value={selectedQuantity}
                        onChange={(e) => setSelectedQuantity(e.target.value)}
                        className="w-24 bg-zinc-900 border-zinc-700 text-white"
                        placeholder="Qté"
                        min="1"
                      />
                      <Button type="button" onClick={handleAddItem} variant="outline" className="border-zinc-700">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>

                    {transportForm.items.length > 0 && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-7 gap-2 text-xs text-zinc-500 px-2">
                          <span>N° ONU</span>
                          <span>Référence</span>
                          <span className="col-span-2">Désignation</span>
                          <span>Classe</span>
                          <span className="text-right">Qté</span>
                          <span className="text-right">NEC (g)</span>
                        </div>
                        {transportForm.items.map((item, index) => (
                          <div key={index} className="grid grid-cols-7 gap-2 items-center p-2 bg-zinc-900 rounded border border-zinc-800">
                            <span className="font-mono text-red-400 text-sm">{item.product?.un_code || '-'}</span>
                            <span className="font-mono text-cyan-400 text-sm">{item.product?.reference}</span>
                            <span className="text-white text-sm col-span-2 truncate">{item.product?.designation}</span>
                            <Badge variant="outline" className={`text-xs ${
                              item.product?.risk_class === '1.1G' ? 'bg-red-500/20 text-red-400' :
                              item.product?.risk_class === '1.3G' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>
                              {item.product?.risk_class || '-'}
                            </Badge>
                            <span className="font-mono text-white text-sm text-right">{item.quantity}</span>
                            <div className="flex items-center justify-end gap-2">
                              <span className="font-mono text-amber-400 text-sm">
                                {((item.product?.nec || 0) * item.quantity).toFixed(3)}
                              </span>
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleRemoveItem(index)}
                                className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}

                        {/* Totals */}
                        <div className="mt-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                          <div className="flex justify-between items-center">
                            <span className="text-amber-400 font-semibold">TOTAL NEC</span>
                            <span className="font-mono text-2xl text-white">{(totals.totalNEC * 1000).toFixed(1)} g</span>
                          </div>
                          <div className="flex justify-between items-center mt-2 text-sm">
                            <span className="text-zinc-400">Poids brut total</span>
                            <span className="font-mono text-zinc-300">{(totals.totalWeight * 1000).toFixed(1)} g</span>
                          </div>
                          <div className="mt-3 pt-3 border-t border-amber-500/20">
                            <span className="text-xs text-zinc-500">NEC par classe:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {Object.entries(totals.necByClass).map(([cls, nec]) => (
                                <Badge key={cls} variant="outline" className="text-xs">
                                  {cls}: {(nec * 1000).toFixed(1)} g
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ADR Warning */}
                  <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="text-red-400 font-semibold mb-1">Déclaration ADR</p>
                        <p className="text-zinc-400">
                          Ce document doit accompagner le transport. L'expéditeur déclare que les marchandises sont correctement classées, emballées, marquées et étiquetées conformément aux réglementations ADR.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                    <Button type="button" variant="outline" onClick={() => setShowTransportDialog(false)} className="border-zinc-700">
                      Annuler
                    </Button>
                    <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black" disabled={generating}>
                      {generating ? (
                        <>
                          <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                          Génération...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" /> Générer PDF
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Bon de Livraison */}
        <Card className="bg-zinc-900/50 border-zinc-800 card-hover">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <FileText className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <CardTitle className="text-white">Bon de Livraison</CardTitle>
                <p className="text-sm text-zinc-400">Document commercial</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-zinc-400">
              Genere un bon de livraison pour accompagner les marchandises livrees au client.
            </p>
            <ul className="text-xs text-zinc-500 space-y-1">
              <li>• Liste detaillee des produits</li>
              <li>• Quantites et references</li>
              <li>• Zone de signature client</li>
            </ul>
            <Dialog open={showBLDialog} onOpenChange={setShowBLDialog}>
              <DialogTrigger asChild>
                <Button className="w-full bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="generate-bl-btn">
                  <ClipboardList className="w-4 h-4 mr-2" /> Generer
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-cyan-400" /> Bon de Livraison
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-sm">Preparation</Label>
                    <Select value={blPrepId} onValueChange={setBlPrepId}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="bl-prep-select">
                        <SelectValue placeholder="Choisir une preparation..." />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {preparations.map(p => (
                          <SelectItem key={p.id} value={p.id} className="text-white">
                            {p.id.slice(-6)} — {p.client || 'Sans client'} ({(p.items || []).length} art.)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {blPrepId && (
                    <div className="bg-zinc-950 rounded-lg p-3 border border-zinc-800 text-xs text-zinc-400">
                      {(() => {
                        const p = preparations.find(pr => pr.id === blPrepId);
                        if (!p) return null;
                        const totalItems = (p.items || []).length;
                        const totalQty = (p.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
                        return (
                          <>
                            <p className="text-white text-sm font-medium mb-1">{p.client || 'Sans client'}</p>
                            <p>{totalItems} articles, {totalQty} unites</p>
                            <p>Statut: <Badge variant="outline" className="text-[10px] ml-1">{p.status}</Badge></p>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => setShowBLDialog(false)} className="flex-1 border-zinc-700">
                      Annuler
                    </Button>
                    <Button className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white" onClick={handleGenerateBL}
                      disabled={!blPrepId || generatingBL} data-testid="confirm-generate-bl">
                      {generatingBL ? (
                        <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> Generation...</>
                      ) : (
                        <><Download className="w-4 h-4 mr-2" /> Generer PDF</>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Export Homologation */}
        <Card className="bg-zinc-900/50 border-zinc-800 card-hover">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Download className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <CardTitle className="text-white">Liste Homologation</CardTitle>
                <p className="text-sm text-zinc-400">Export réglementaire</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-zinc-400">
              Exporte la liste complète des produits avec leurs numéros d'homologation pour la déclaration poudre.
            </p>
            <ul className="text-xs text-zinc-500 space-y-1">
              <li>• N° homologation CE</li>
              <li>• Codes ONU et classifications</li>
              <li>• NEC par produit</li>
              <li>• Format Excel</li>
            </ul>
            <Button 
              className="w-full bg-green-500/20 text-green-400 hover:bg-green-500/30"
              onClick={async () => {
                try {
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exports/homologation`, {
                    credentials: 'include'
                  });
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `liste_homologation_${new Date().toISOString().slice(0, 10)}.xlsx`;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                  toast.success('Export téléchargé');
                } catch (error) {
                  toast.error('Erreur lors de l\'export');
                }
              }}
            >
              <Download className="w-4 h-4 mr-2" /> Exporter Excel
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DocumentsPage;
